/* eslint-disable camelcase */
import { type UseMutationResult, useMutation } from 'react-query';

import {
  CustomReportsService,
  Wealthlane_ReportService_Dtos_CustomReport_CustomReportSaveAndRunResponseDto,
} from '@wealthlane/api-services/wealthlane-report-service';
import { toast } from 'react-toastify';
import ToastComponent from '../../components/toast';
import _ from 'lodash';

const mapUserFieldFormats = (item: any) => ({
  ...item,
  userFieldFormats: item?.userFieldFormats?.map((format: any) => ({
    key: format.key,
    value: !_.isEmpty(format.value) ? format.value : format.placeholder,
    placeholder: format.placeholder,
  })),
});

/**
 * Builds fieldBuckets from the "Selected output columns" panel items only.
 * Array order defines the display sequence — no sequenceNumber sent.
 * (HTF-1204 / HTF-1214)
 */
const buildFieldBuckets = (payload: Record<string, any>) => {
  // Required fields are always included (they're always in selected output columns)
  const required = (payload.requiredFieldBuckets ?? []).map(mapUserFieldFormats);

  // Optional fields — only those explicitly selected by the user
  const optional = (payload.optionalFieldBuckets ?? [])
    .filter((f: any) => f.isSelected === true)
    .map(mapUserFieldFormats);

  // UDF fields — only those explicitly selected
  const udf = (payload.udfFieldBuckets ?? [])
    .filter((item: any) => item.isSelected === true);

  const selected = [...required, ...optional, ...udf];

  // Sort by sequenceNumber to match the visual DnD order
  selected.sort((a, b) => (a.sequenceNumber ?? 0) - (b.sequenceNumber ?? 0));

  // Strip sequenceNumber — backend uses array position only
  return selected.map(({ sequenceNumber, ...field }) => field);
};

const transformPayload = (payload: Record<string, any>) => {
  const fieldBuckets = buildFieldBuckets(payload);

  const { requiredFieldBuckets, optionalFieldBuckets, ...rest } = payload;
  return { ...rest, fieldBuckets };
};

const usePostSubmitAndRunDynamicReport = (
  handleRequest: <T>(request: Promise<T>) => Promise<T | undefined>,
  openSubmitModal: () => void,
  setTaskReferanceNo?: any
): UseMutationResult<
  Wealthlane_ReportService_Dtos_CustomReport_CustomReportSaveAndRunResponseDto | undefined,
  unknown,
  any,
  unknown
> => {
  return useMutation(
    async (payload: any) => {
      let newPayload = { ...payload, reportConfigurationId: payload.id };
      newPayload = transformPayload(newPayload);
      return await handleRequest(
        CustomReportsService.postApiReportApiAppCustomReportsSubmitAndRunReport(newPayload)
      );
    },
    {
      onSuccess: async (
        response:
          | Wealthlane_ReportService_Dtos_CustomReport_CustomReportSaveAndRunResponseDto
          | undefined
      ) => {
        if (response && response?.isSuccess === true) {
          openSubmitModal();
          setTaskReferanceNo(response.taskRefrenceNo);
        } else if (response && response?.error) {
          toast.error(<ToastComponent label='Error' message={`${response?.error[0]}`} />);
        }
      },
      onError: (err) => {
        console.log('error', err);
      },
    }
  );
};

export default usePostSubmitAndRunDynamicReport;
